/**
 * Editor completo de um Event Type (configurações + disponibilidade + exceções)
 * 
 * Organizado em tabs:
 * - Geral: Configurações básicas do evento
 * - Disponibilidade: Grade semanal de horários
 * - Exceções: Bloqueios e horários especiais por data
 * 
 * RELEASE: 2026-02-14
 * VERSION: 1.0.0
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Settings2, Clock, CalendarOff } from 'lucide-react';
import { stripTemplateVariables } from '@/lib/schedulingUtils';
import {
  type SchedulingEventType,
} from '@/hooks/useSchedulingEventTypes';
import { EventTypeGeneralTab } from './EventTypeGeneralTab';
import { EventTypeAvailabilityTab } from './EventTypeAvailabilityTab';
import { EventTypeDateOverridesTab } from './EventTypeDateOverridesTab';


interface EventTypeEditorProps {
  /** O event type sendo editado */
  eventType: SchedulingEventType;
  /** Callback para voltar à listagem */
  onBack: () => void;
}

export function EventTypeEditor({ eventType, onBack }: EventTypeEditorProps) {
  return (
    <div className="space-y-6">
      {/* Header com botão voltar */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-foreground truncate">
            {stripTemplateVariables(eventType.title)}
          </h2>
          <p className="text-sm text-muted-foreground">
            /{eventType.slug} • {eventType.duration_minutes} min
          </p>
        </div>
        <div
          className="h-4 w-4 rounded-full shrink-0"
          style={{ backgroundColor: eventType.color || '#3B82F6' }}
        />
      </div>

      {/* Tabs de edição */}
      <Tabs defaultValue="general" className="space-y-4">
        <TabsList className="bg-muted/50 p-1">
          <TabsTrigger value="general" className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <Settings2 className="h-4 w-4" />
            Geral
          </TabsTrigger>
          <TabsTrigger value="availability" className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <Clock className="h-4 w-4" />
            Disponibilidade
          </TabsTrigger>
          <TabsTrigger value="overrides" className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <CalendarOff className="h-4 w-4" />
            Exceções
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <EventTypeGeneralTab eventType={eventType} />
        </TabsContent>

        <TabsContent value="availability">
          <EventTypeAvailabilityTab eventTypeId={eventType.id} />
        </TabsContent>

        <TabsContent value="overrides">
          <EventTypeDateOverridesTab eventTypeId={eventType.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
